/**
 * Still frames from a video file, taken in the browser (flow module phase 5):
 * the clip is never uploaded — only these small JPEGs are. Frames are spread
 * evenly over the clip, scaled to at most `maxEdge` px.
 */
export interface ExtractedFrames {
  readonly frames: Blob[];
  readonly durationSeconds: number;
}

/** Longest clip read (seconds): a pump cycle fits, a long recording is cut. */
export const MAX_CLIP_SECONDS = 60;

function once(target: HTMLMediaElement, event: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const ok = (): void => {
      cleanup();
      resolve();
    };
    const fail = (): void => {
      cleanup();
      reject(new Error('video could not be read'));
    };
    const cleanup = (): void => {
      target.removeEventListener(event, ok);
      target.removeEventListener('error', fail);
    };
    target.addEventListener(event, ok);
    target.addEventListener('error', fail);
  });
}

export async function extractFrames(file: Blob, count = 8, maxEdge = 640): Promise<ExtractedFrames> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.src = url;
  try {
    await once(video, 'loadeddata');
    const duration = Math.min(Number.isFinite(video.duration) ? video.duration : 0, MAX_CLIP_SECONDS);
    if (!(duration > 0) || !video.videoWidth || !video.videoHeight) throw new Error('video has no frames');
    const scale = Math.min(1, maxEdge / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no canvas');
    const frames: Blob[] = [];
    for (let i = 0; i < count; i++) {
      // Evenly spread, away from the very first and last frame.
      video.currentTime = (duration * (i + 0.5)) / count;
      await once(video, 'seeked');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.75));
      if (blob) frames.push(blob);
    }
    return { frames, durationSeconds: duration };
  } finally {
    URL.revokeObjectURL(url);
    video.removeAttribute('src');
  }
}
